import React, { useRef, useState } from "react";
import { adminFetch } from "./auth";

const API = process.env.REACT_APP_BACKEND_URL;

export const uid = () => Math.random().toString(36).slice(2, 10);

export function resolveSrc(src) {
  return src && src.startsWith("/api/") ? `${API}${src}` : src;
}

export function WorkflowBar({ status, onSaveDraft, onPreview, onPublish, onDiscard, busy }) {
  const isDraft = status === "draft";
  return (
    <div className="admin-workflow-bar" data-testid="workflow-bar">
      <span
        className={`admin-badge admin-badge-${isDraft ? "draft" : "published"}`}
        data-testid="content-status-badge"
      >
        {isDraft ? "● Unpublished draft changes" : "● Published — live on site"}
      </span>
      <div className="admin-workflow-actions">
        {isDraft && onDiscard ? (
          <button className="admin-mini danger" disabled={busy} onClick={onDiscard} data-testid="discard-button">
            Discard Draft
          </button>
        ) : null}
        <button className="admin-mini" disabled={busy} onClick={onSaveDraft} data-testid="save-draft-button">
          Save Draft
        </button>
        <button className="admin-mini" disabled={busy} onClick={onPreview} data-testid="preview-button">
          Preview Changes
        </button>
        <button className="button button-primary" disabled={busy} onClick={onPublish} data-testid="publish-button">
          Publish Changes
        </button>
      </div>
    </div>
  );
}

export function Field({ label, value, onChange, testid, placeholder, type = "text" }) {
  return (
    <label>
      {label}
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
      />
    </label>
  );
}

export function TextArea({ label, value, onChange, testid, rows = 4, placeholder }) {
  return (
    <label>
      {label}
      <textarea
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
      />
    </label>
  );
}

export function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="admin-check">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      {label}
    </label>
  );
}

export function Accordion({ title, subtitle, children, defaultOpen = false }) {
  return (
    <details className="admin-accordion" open={defaultOpen}>
      <summary>
        <span>{title}</span>
        {subtitle ? <em>{subtitle}</em> : null}
      </summary>
      <div className="admin-accordion-body">{children}</div>
    </details>
  );
}

export function ImageField({ image, onChange, onStatus, testid, label = "Image" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const img = image || {};
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const d = await adminFetch("/api/admin/upload", { method: "POST", body: fd });
      onChange({ ...img, src: d.url });
      onStatus && onStatus("success", "Image uploaded. Save Draft, then Publish to make it live.");
    } catch (err) {
      onStatus && onStatus("error", err.message);
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="admin-subblock">
      <h3>{label}</h3>
      <div className="admin-hero-preview">{img.src ? <img src={resolveSrc(img.src)} alt={img.alt || "preview"} /> : null}</div>
      <input
        value={img.alt || ""}
        onChange={(e) => onChange({ ...img, alt: e.target.value })}
        placeholder="Image description (alt text)"
        data-testid={`${testid}-alt`}
      />
      <input ref={ref} type="file" accept="image/*" style={{ display: "none" }} onChange={onFile} data-testid={`${testid}-file`} />
      <button className="admin-mini" disabled={busy} onClick={() => ref.current?.click()} data-testid={`${testid}-upload`}>
        {busy ? "Uploading…" : img.src ? "Replace Image" : "Upload Image"}
      </button>
    </div>
  );
}

export function ButtonListEditor({ buttons, onChange, prefix }) {
  const list = buttons || [];
  const set = (i, k, v) => onChange(list.map((b, idx) => (idx === i ? { ...b, [k]: v } : b)));
  return (
    <div className="admin-subblock">
      <h3>Buttons</h3>
      {list.map((b, i) => (
        <div className="admin-card-editor" key={i}>
          <div className="admin-row">
            <input value={b.label || ""} onChange={(e) => set(i, "label", e.target.value)} placeholder="Button label" data-testid={`${prefix}-label-${i}`} />
            <input value={b.href || ""} onChange={(e) => set(i, "href", e.target.value)} placeholder="Link (e.g. /rates or /book)" data-testid={`${prefix}-href-${i}`} />
          </div>
          <div className="admin-row">
            <select value={b.variant || "primary"} onChange={(e) => set(i, "variant", e.target.value)} data-testid={`${prefix}-variant-${i}`}>
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
              <option value="light">Light</option>
            </select>
            <button className="admin-mini danger" onClick={() => { if (window.confirm("Remove this button?")) onChange(list.filter((_, idx) => idx !== i)); }} data-testid={`${prefix}-delete-${i}`}>Remove</button>
          </div>
        </div>
      ))}
      <button className="admin-mini" onClick={() => onChange([...list, { label: "", href: "/", variant: "secondary" }])} data-testid={`${prefix}-add`}>+ Add Button</button>
    </div>
  );
}
